import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { FormAlert, useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import {
	FileData,
	ProspectEmploymentContract,
} from '@/libs/fleet-api/services/types';
import {
	addEmploymentContract,
	deleteEmploymentContract,
	getPraSurveyDetail,
	updateEmploymentContract,
} from '@/libs/fleet-api/services/survey';
import { formatCurrency } from '@/libs/antd/formater';
import {
	useSelectBouwheer,
	useSelectPaymentMethod,
} from '@/libs/fleet-api/hooks/master-data';
import { inputThousand } from '@/libs/antd/input-number';
import dayjs from 'dayjs';
import { uploader } from '@/libs/fleet-api/uploader';
import { useModals } from '@/components/modals/ModalProvider';

type OfficeObservationContract = ProspectEmploymentContract<'payload'> &
	Pick<
		ProspectEmploymentContract,
		'bouwheer' | 'industrySector' | 'contractDocument' | 'paymentMethod'
	>;

type VisitReportPhotoForm = {
	input?: OfficeObservationContract;
	lists: OfficeObservationContract[];
};

const FRMVisitReportBouwheer: FC = () => {
	const modalAPI = useModals();
	const { mainInformation } = useFRMVisitReportPageContext();
	const { form, value, fieldName } = useForm<VisitReportPhotoForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const { openEmploymentContractModal } = useModals();
	const selectBouwheer = useSelectBouwheer();
	const selectPaymentMethod = useSelectPaymentMethod();

	const { data, isLoading } = useQuery({
		queryKey: ['getPraSurveyDetail', mainInformation?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPraSurveyDetail(id);
		},
		enabled: !!mainInformation?.prospectId,
	});

	const addEmploymentContractMutation = useMutation({
		mutationFn: addEmploymentContract,
	});

	const updateEmploymentContractMutation = useMutation({
		mutationFn: updateEmploymentContract,
	});

	const deleteEmploymentContractMutation = useMutation({
		mutationFn: deleteEmploymentContract,
	});

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	if (isLoading) return null;

	const { employmentContracts } = data?.data ?? {};

	return (
		<Form.Provider>
			<Typography.Title level={3}>
				Informasi Bouwheer (Perusahaan/Perorangan)
			</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists:
							employmentContracts?.map((record) => {
								return {
									...record,
									bouwheerId: record.bouwheer.id,
									paymentMethod: record.paymentMethod,
								};
							}) ?? [],
					} as VisitReportPhotoForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						try {
							await updateEmploymentContractMutation.mutateAsync({
								id: input!.id,
								prospectId: mainInformation!.prospectId,
								bouwheerId: input!.bouwheerId,
								contractDetail: input!.contractDetail,
								contractValue: input!.contractValue,
								endDate: input!.endDate,
								startDate: input!.startDate,
								jobLocation: input!.jobLocation,
								jobTitle: input!.jobTitle,
								spkNumber: input!.spkNumber,
								termOfPayment: input?.termOfPayment,
								paymentMethod: input?.paymentMethod?.id,
								tenor: input?.tenor,
								contractDocument: {
									id: input!.contractDocument!.id,
									name: input!.contractDocument!.name,
									path: input!.contractDocument!.path,
								},
							});
							form.setFieldsValue({
								lists: lists.map((list, index) => {
									return index === modal.index ? input! : list;
								}),
							});
						} catch (error) {
							console.log(error);
						}
					} else {
						try {
							const res = await addEmploymentContractMutation.mutateAsync({
								prospectId: mainInformation!.prospectId,
								bouwheerId: input!.bouwheerId,
								contractDetail: input!.contractDetail,
								contractValue: input!.contractValue,
								endDate: input!.endDate,
								startDate: input!.startDate,
								jobLocation: input!.jobLocation,
								jobTitle: input!.jobTitle,
								spkNumber: input!.spkNumber,
								termOfPayment: input?.termOfPayment,
								paymentMethod: input!.paymentMethod?.id,
								tenor: input!.tenor,
								contractDocument: {
									id: input!.contractDocument!.id,
									name: input!.contractDocument!.name,
									path: input!.contractDocument!.path,
								},
							});
							form.setFieldsValue({
								lists: [...lists, { ...input!, id: res.data.id }],
							});
						} catch (error) {
							console.log(error);
						}
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportPhotoForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addEmploymentContractMutation.isPending ||
											updateEmploymentContractMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									scroll={{
										x: 2000,
									}}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	openEmploymentContractModal(
																		record as OfficeObservationContract,
																	);
																}}
															>
																<FileSearchOutlined />
															</a>
															<div
																style={{
																	display: 'flex',
																	flexWrap: 'nowrap',
																	justifyContent: 'center',
																}}
															>
																<Divider type="vertical" />
															</div>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalAPI.modal.confirm({
																			title: 'delete this data?',
																		});

																	if (confirmDelete) {
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																		deleteEmploymentContractMutation.mutateAsync(
																			record.id!,
																		);
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Bouwheer',
											render: (_, record) => record.bouwheer.name ?? '-',
										},
										{
											title: 'Bouwheer ID',
											render: (_, record) => record.bouwheerId ?? '-',
										},
										{
											title: 'Kelas Industri',
											render: (_, record) =>
												record.bouwheer.industrySector?.industryClass ?? '-',
										},
										{
											title: 'Nama Pekerjaan',
											render: (_, record) => record.jobTitle ?? '-',
										},
										{
											title: 'No. SPK',
											render: (_, record) => record.spkNumber ?? '-',
										},
										{
											title: 'No. SPK',
											render: (_, record) => record.spkNumber ?? '-',
										},
										{
											title: 'Lokasi Pekerjaan',
											render: (_, record) => record.jobLocation ?? '-',
										},
										{
											title: 'Detail Kontrak',
											render: (_, record) => record.contractDetail ?? '-',
										},
										{
											title: 'Nilai Kontrak',
											render: (_, record) =>
												formatCurrency(record.contractValue) ?? '-',
										},
										{
											title: 'Tanggal Mulai',
											render: (_, record) =>
												(record.startDate &&
													dayjs(record.startDate).format('DD/MM/YYYY')) ??
												'-',
										},
										{
											title: 'Tanggal Berakhir',
											render: (_, record) =>
												(record.endDate &&
													dayjs(record.endDate).format('DD/MM/YYYY')) ??
												'-',
										},
										{
											title: 'Tenor',
											render: (_, record) => record.tenor ?? '-',
										},
										{
											title: 'Sistem Pembayaran',
											render: (_, record) => record.paymentMethod?.value ?? '-',
										},
										{
											title: 'TOP Pembayaran',
											render: (_, record) => record.termOfPayment ?? '-',
										},
									]}
								/>
								<FormAlert
									alerts={
										!value?.lists?.length
											? ['Informasi Bouwheer wajib diisi']
											: []
									}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Kontrak Kerja</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'bouwheer'])}
												noStyle
											/>
											<Form.Item
												label="Bouwheer"
												name={fieldName(['input', 'bouwheerId'])}
												labelCol={{ span: 10 }}
											>
												<Select
													{...selectBouwheer}
													options={
														!values.input?.bouwheerId ||
														selectBouwheer.options?.length
															? selectBouwheer.options
															: [
																	{
																		label: values.input.bouwheer.name,
																		value: values.input.bouwheer.id,
																	},
																]
													}
													onSelect={(value, option) => {
														selectBouwheer.onSelect!(value, option);
														setFieldValue(
															fieldName(['input', 'bouwheer']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Bouwheer Name"
												name={fieldName(['input', 'bouwheer', 'name'])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Bouwheer Type"
												name={fieldName(['input', 'bouwheer', 'type'])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Kelas Industri"
												name={fieldName([
													'input',
													'bouwheer',
													'industrySector',
													'industryClass',
												])}
												labelCol={{ span: 10 }}
											>
												<Input disabled />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Nama Pekerjaan"
												name={fieldName(['input', 'jobTitle'])}
												labelCol={{ span: 10 }}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="No. SPK"
												name={fieldName(['input', 'spkNumber'])}
												labelCol={{ span: 10 }}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Lokasi Pekerjaan"
												name={fieldName(['input', 'jobLocation'])}
												labelCol={{ span: 10 }}
											>
												<Input />
											</Form.Item>
										</Col>
										{/* <Col span={12}>
											<Form.Item
												label="Term Of Payment"
												name={fieldName(['input', 'termOfPayment'])}
												labelCol={{ span: 10 }}
											>
												<Input />
											</Form.Item>
										</Col> */}
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												label="Detail Kontrak"
												name={fieldName(['input', 'contractDetail'])}
												labelCol={{ span: 5 }}
												wrapperCol={{
													style: {
														marginLeft: -10,
													},
												}}
											>
												<Input.TextArea rows={4} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Nilai Kontrak"
												name={fieldName(['input', 'contractValue'])}
												labelCol={{ span: 10 }}
											>
												<InputNumber {...inputThousand} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'contractDocument'])}
												label="Upload Dokumen Kontrak Kerja"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => {
													return {
														id: file.uid,
														format: file.type,
														name: file.name,
														url: file.thumbUrl,
													} as FileData;
												}}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													beforeUpload={() => false}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'contractDocument']),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/employment-contract`;

														try {
															const url = await uploadMutation.mutateAsync({
																file: file as unknown as File,
																fileid,
																filepath,
															});

															form.setFieldValue(
																fieldName(['input', 'contractDocument']),
																{
																	format: 'jpeg',
																	id: fileid,
																	name: file.name,
																	path: filepath,
																	url,
																},
															);
														} catch {
															form.setFieldValue(
																fieldName(['input', 'contractDocument']),
																null,
															);
														}
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
									<Divider />

									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Tanggal Mulai"
												name={fieldName(['input', 'startDate'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Tanggal Berakhir"
												name={fieldName(['input', 'endDate'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY-MM-DD')
												}
											>
												<DatePicker />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Tenor"
												name={fieldName(['input', 'tenor'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Sistem Pembayaran"
												name={fieldName(['input', 'paymentMethod'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueFromEvent={(_, option) => option?.data}
												getValueProps={(value) => {
													if (!value) return {};
													return {
														value: {
															value: value.id,
															label: value.value,
														},
													};
												}}
											>
												<Select {...selectPaymentMethod} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="TOP Pembayaran"
												name={fieldName(['input', 'termOfPayment'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMVisitReportBouwheer;
